import { useEffect, useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const textColorFor = (color) => {
  const hex = /^#[0-9A-Fa-f]{6}$/.test(color || "") ? color.slice(1) : "FDFBF7";
  const [r, g, b] = [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16));
  return (r * 299 + g * 587 + b * 114) / 1000 > 164 ? "#24160F" : "#FFFFFF";
};

const imageSource = (menu) => {
  if (!menu.cover_image) return "";
  return menu.cover_image.startsWith("http") ? menu.cover_image : `${BACKEND_URL}/api/files/${menu.cover_image}`;
};

const MenuIndexLoading = ({ background = "#FDFBF7" }) => (
  <div className="fixed inset-0 z-[100] flex min-h-screen items-center justify-center" style={{ background }} data-testid="public-menu-index-images-loading">
    <div className="flex flex-col items-center gap-4" style={{ color: "#8B5E3C" }}>
      <div className="h-[34px] w-[34px] animate-spin rounded-full border-2 border-[#D8C9BA] border-t-[#8B5E3C]" data-testid="public-menu-index-images-loading-spinner" />
      <span className="font-template text-xl" data-testid="public-menu-index-images-loading-label">Cargando menús</span>
    </div>
  </div>
);

export default function PublicMenuIndex() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loadedImageSources, setLoadedImageSources] = useState([]);

  useEffect(() => {
    api.public.getMenuIndex().then((response) => setData(response.data)).catch((err) => setError(err.response?.data?.detail || "No fue posible cargar los menús"));
  }, []);

  const colors = useMemo(() => {
    const background = data?.settings?.background_color || "#FDFBF7";
    const foreground = textColorFor(background);
    return { background, foreground, muted: foreground === "#FFFFFF" ? "rgba(255,255,255,0.78)" : "#725F53", line: foreground === "#FFFFFF" ? "rgba(255,255,255,0.28)" : "#D8C9BA" };
  }, [data]);
  const imageSources = useMemo(() => {
    if (!data) return [];
    const logo = data.settings.logo_path ? `${BACKEND_URL}/api/files/${data.settings.logo_path}` : "";
    return [...new Set([logo, ...data.menus.map(imageSource)].filter(Boolean))];
  }, [data]);
  const imageSourcesKey = imageSources.join("|");
  const imagesPending = Boolean(data && imageSources.some((source) => !loadedImageSources.includes(source)));

  useEffect(() => {
    setLoadedImageSources([]);
  }, [imageSourcesKey]);

  const markImageLoaded = (source) => {
    setLoadedImageSources((previous) => previous.includes(source) ? previous : [...previous, source]);
  };

  if (!data && !error) return <MenuIndexLoading />;
  if (error) return <div className="flex min-h-screen items-center justify-center px-6 text-center" style={{ background: "#FDFBF7", color: "#24160F" }} data-testid="public-menu-index-error">{error}</div>;

  return (<>
    <main className="min-h-screen px-5 py-10 sm:px-10 sm:py-16" style={{ backgroundColor: colors.background, color: colors.foreground, visibility: imagesPending ? "hidden" : "visible" }} data-testid="public-menu-index-page">
      <div className="mx-auto max-w-3xl">
        {data.settings.logo_path && (
          <header className="border-b py-3 text-center sm:py-5" style={{ borderColor: colors.line }} data-testid="public-menu-index-header">
            <img src={`${BACKEND_URL}/api/files/${data.settings.logo_path}`} alt="Logo de la empresa" loading="eager" decoding="async" onLoad={() => markImageLoaded(`${BACKEND_URL}/api/files/${data.settings.logo_path}`)} onError={() => markImageLoaded(`${BACKEND_URL}/api/files/${data.settings.logo_path}`)} className="mx-auto h-[92px] max-w-[322px] object-contain" data-testid="public-menu-index-logo" />
          </header>
        )}
        <section className="mx-auto grid max-w-[620px] gap-4 py-8" data-testid="public-menu-index-list">
          {data.menus.map((menu) => {
            const image = imageSource(menu);
            return <a key={menu.id} href={`/menu/${menu.slug}`} className="group relative block h-40 overflow-hidden border p-5 transition-transform duration-200 hover:-translate-y-1 sm:h-44 sm:p-6" style={{ borderColor: colors.line, color: "#FFFFFF", background: image ? "#24160F" : "#8B5E3C" }} data-testid={`public-menu-index-item-${menu.id}`}>
              {image && <img src={image} alt="" loading="eager" decoding="async" onLoad={() => markImageLoaded(image)} onError={() => markImageLoaded(image)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`public-menu-index-image-${menu.id}`} />}
              {image && <span className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(20,12,8,0.08), rgba(20,12,8,0.88) 88%)" }} />}
              <span className="relative flex h-full flex-col items-end justify-center pl-20 text-right">
                <ArrowUpRight className="absolute right-0 top-0 h-6 w-6" data-testid={`public-menu-index-link-icon-${menu.id}`} />
                <h2 className="font-template text-3xl font-bold leading-none sm:text-4xl" data-testid={`public-menu-index-name-${menu.id}`}>{menu.name}</h2>
                {menu.description && <p className="mt-2 max-w-sm text-sm leading-5 text-white/85" data-testid={`public-menu-index-description-${menu.id}`}>{menu.description}</p>}
              </span>
            </a>;
          })}
        </section>
        {data.menus.length === 0 && <p className="py-12 text-center text-base" style={{ color: colors.muted }} data-testid="public-menu-index-empty">No hay menús disponibles en este momento.</p>}
      </div>
    </main>
    {imagesPending && <MenuIndexLoading background={colors.background} />}
  </>);
}